import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { PROCEDURES_PAGE_ACCESS as access } from '@/hooks/procedures/useProceduresPermissions';
import { ProceduresGate } from './components/ProceduresGate';

// Eager load dashboard for instant access
import ProceduresDashboard from './dashboard';

// Lazy load all other pages
const ProceduresListPage = lazy(() => import('./procedures'));
const ImportPage = lazy(() => import('./import'));
const EditorPage = lazy(() => import('./editor'));
const RunsPage = lazy(() => import('./runs'));
const ProblemsPage = lazy(() => import('./problems'));
const QrSheetPage = lazy(() => import('./qr-sheet'));
const CategoriesPage = lazy(() => import('./categories'));
const SettingsPage = lazy(() => import('./settings'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/** /app/procedures/:id only for numeric ids; anything else goes back to the list. */
function EditorRoute() {
  const { id } = useParams();
  if (!id || !/^\d+$/.test(id)) return <Navigate to="/app/procedures/list" replace />;
  return (
    <ProceduresGate allow={access.editor}>
      <EditorPage key={id} procedureId={Number(id)} />
    </ProceduresGate>
  );
}

/**
 * Procedures Management Routes
 *
 * Module access (Admin / ProceduresAdmin / ProceduresEditor) is the domain gate; inside, pages gate on
 * capabilities (role OR Procedures.* right) via ProceduresGate. The per-page predicates live in
 * PROCEDURES_PAGE_ACCESS, which the sidebar menu (proceduresMenu.tsx) reads too, so a page shows in the
 * menu exactly when its route lets the person in.
 */
export default function ProceduresManagementRoutes() {
  return (
    <MainLayout domainKey="procedures">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<ProceduresGate allow={access.dashboard}><ProceduresDashboard /></ProceduresGate>} />

          {/* Procedures */}
          <Route path="/list" element={<ProceduresGate allow={access.list}><ProceduresListPage /></ProceduresGate>} />
          <Route path="/import" element={<ProceduresGate allow={access.import}><ImportPage /></ProceduresGate>} />
          <Route path="/qr-sheet" element={<ProceduresGate allow={access.qrSheet}><QrSheetPage /></ProceduresGate>} />

          {/* Activity */}
          <Route path="/runs" element={<ProceduresGate allow={access.runs}><RunsPage /></ProceduresGate>} />
          <Route path="/problems" element={<ProceduresGate allow={access.problems}><ProblemsPage /></ProceduresGate>} />

          {/* Administration */}
          <Route path="/categories" element={<ProceduresGate allow={access.categories}><CategoriesPage /></ProceduresGate>} />
          <Route path="/settings" element={<ProceduresGate allow={access.settings}><SettingsPage /></ProceduresGate>} />

          {/* Editor (numeric id) */}
          <Route path="/:id" element={<EditorRoute />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/procedures" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
